//Frontend/src/unicodex_ui/pages/ManufacturerDashboardPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ManufacturerNav from "../components/ManufacturerNav";
import portalApi from "../services/portalApi";
import "../styles/ManufacturerPortal.css";
import "../styles/ManufacturerDashboardPage.css";

function ManufacturerDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [me, setMe] = useState(null);

  const [productCount, setProductCount] = useState(0);
  const [batchCount, setBatchCount] = useState(0);

  const navigate = useNavigate();

  function handleSignOut() {
    localStorage.removeItem("ucx_mfg_token");
    localStorage.removeItem("ucx_mfg_user");
    navigate("/portal/login", { replace: true });
  }

  const displayName = useMemo(() => {
    const stored = localStorage.getItem("ucx_mfg_user");
    if (!stored) return "Manufacturer";
    try {
      const u = JSON.parse(stored);
      return u?.name || u?.email || "Manufacturer";
    } catch {
      return "Manufacturer";
    }
  }, []);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const meRes = await portalApi.getMe();
        if (!meRes?.ok) throw new Error("me_failed");

        const [pRes, bRes] = await Promise.all([
          portalApi.listProducts(),
          portalApi.listBatches(),
        ]);

        if (!alive) return;

        setMe(meRes);
        setProductCount(Array.isArray(pRes?.products) ? pRes.products.length : 0);
        setBatchCount(Array.isArray(bRes?.batches) ? bRes.batches.length : 0);
      } catch (e) {
        console.error("[ManufacturerDashboard] load error:", e);
        if (!alive) return;
        setError("Unable to load portal data. Please sign in again.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  const manufacturer = me?.manufacturer || null;

  return (
    <section className="ucx-section ucx-mfg-portal-page">
      <div className="ucx-container">
        <ManufacturerNav
          title="Manufacturer Portal"
          subtitle={displayName}
          onSignOut={handleSignOut}
        />

        <div className="ucx-mfg-panel">
          <div className="ucx-mfg-panel-head">
            <div>
              <h1 className="ucx-mfg-title">Overview</h1>
              <p className="ucx-mfg-subtitle">
                Manage your products, batches, and UNI-CODEX code generation.
              </p>
            </div>

            <div className="ucx-mfg-actions">
              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={() => navigate("/portal/products")}
              >
                Products
              </button>
              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={() => navigate("/portal/batches")}
              >
                Batches
              </button>
              <button
                type="button"
                className="ucx-btn ucx-btn-primary"
                onClick={() => navigate("/portal/codes")}
              >
                Codes
              </button>
            </div>
          </div>

          {error && <div className="ucx-mfg-error">{error}</div>}

          {loading ? (
            <div className="ucx-mfg-empty">Loading…</div>
          ) : (
            <>
              <div className="ucx-mfg-grid">
                <div className="ucx-mfg-card">
                  <div className="ucx-mfg-card-label">Manufacturer</div>
                  <div className="ucx-mfg-card-value">
                    {manufacturer?.legal_name || "—"}
                  </div>
                  <div className="ucx-mfg-card-meta">
                    Prefix: <b>{manufacturer?.prefix || "—"}</b>
                  </div>
                </div>

                <div className="ucx-mfg-card">
                  <div className="ucx-mfg-card-label">Products</div>
                  <div className="ucx-mfg-card-value">{productCount}</div>
                  <div className="ucx-mfg-card-meta">Registered in your account</div>
                </div>

                <div className="ucx-mfg-card">
                  <div className="ucx-mfg-card-label">Batches</div>
                  <div className="ucx-mfg-card-value">{batchCount}</div>
                  <div className="ucx-mfg-card-meta">Created for your products</div>
                </div>
              </div>

              <div className="ucx-mfg-note">
                Tip: Create a product → create a batch → generate codes → print labels.
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default ManufacturerDashboardPage;
